/**
 * Support Page
 *
 * Public page - accessible without authentication. Used as the Support URL for
 * the CCMS mobile apps on the Apple App Store and Google Play. Not linked from
 * the login footer (reached directly by its URL).
 */

import {
  Stack,
  Group,
  SimpleGrid,
  Card,
  ThemeIcon,
  Title,
  Text,
  List,
  Button,
  Anchor,
  Badge,
  rem,
} from '@mantine/core';
import {
  IconLifebuoy,
  IconMail,
  IconLogin2,
  IconBellRinging,
  IconUserX,
  IconClipboardCheck,
} from '@tabler/icons-react';
import PublicPageLayout from './PublicPageLayout';

const SUPPORT_EMAIL = 'developer@actschurch.co.za';

const TOPICS = [
  {
    icon: IconLogin2,
    title: 'Signing in',
    text: 'The apps require an account provided by your organisation. If you can’t sign in, check your connection, then contact us so we can verify your account.',
  },
  {
    icon: IconBellRinging,
    title: 'Notifications',
    text: 'Manage notification permissions in your device settings, and your in-app notification preferences under Settings → Notifications.',
  },
  {
    icon: IconUserX,
    title: 'Delete your account',
    text: 'Request account deletion in the app at Settings → Legal & account → Delete account, or from our data-deletion page.',
  },
];

export default function SupportPage() {
  return (
    <PublicPageLayout
      eyebrow="CCMS Apps · Support"
      title="We’re here to help"
      subtitle="Have a question or hit a snag? Reach our team and browse quick answers to the most common topics."
      icon={<IconLifebuoy size={28} />}
      actions={
        <Button
          component="a"
          href={`mailto:${SUPPORT_EMAIL}`}
          radius="xl"
          size="md"
          variant="white"
          color="dark"
          leftSection={<IconMail size={18} />}
        >
          Email support
        </Button>
      }
    >
      {/* Contact + response time */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Card withBorder radius="lg" padding="xl" shadow="sm">
          <ThemeIcon size={46} radius="md" variant="light"><IconMail size={24} /></ThemeIcon>
          <Text fw={700} mt="md" size="lg">Contact us</Text>
          <Text c="dimmed" size="sm" mt={4}>
            The fastest way to reach us is by email. We aim to respond within two business days.
          </Text>
          <Anchor href={`mailto:${SUPPORT_EMAIL}`} fw={600} mt="sm" style={{ display: 'inline-block' }}>
            {SUPPORT_EMAIL}
          </Anchor>
        </Card>

        <Card withBorder radius="lg" padding="xl" shadow="sm">
          <ThemeIcon size={46} radius="md" variant="light"><IconClipboardCheck size={24} /></ThemeIcon>
          <Text fw={700} mt="md" size="lg">Before you contact us</Text>
          <Text c="dimmed" size="sm" mt={4}>Including these details helps us resolve your issue faster:</Text>
          <List size="sm" spacing={4} mt="sm" c="dimmed">
            <List.Item><b>Your account</b> — the email or username you sign in with.</List.Item>
            <List.Item><b>Device</b> — the phone, tablet or browser you used.</List.Item>
            <List.Item><b>App &amp; OS version</b> — from the app’s Settings and your device.</List.Item>
            <List.Item><b>What happened</b> — what you tried, expected, and saw (a screenshot helps).</List.Item>
          </List>
        </Card>
      </SimpleGrid>

      {/* Common topics */}
      <Stack gap="lg">
        <Stack gap={4} align="center" ta="center">
          <Badge variant="light" radius="sm" size="lg">Quick answers</Badge>
          <Title order={2} style={{ fontSize: rem(28), letterSpacing: '-0.3px' }}>Common topics</Title>
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          {TOPICS.map((t) => (
            <Card key={t.title} withBorder radius="lg" padding="lg" shadow="sm">
              <ThemeIcon size={42} radius="md" variant="light"><t.icon size={22} /></ThemeIcon>
              <Text fw={700} mt="md">{t.title}</Text>
              <Text c="dimmed" size="sm" mt={4}>{t.text}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      {/* Legal */}
      <Card withBorder radius="lg" padding="lg" bg="var(--mantine-color-gray-light)">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Text fw={600}>Looking for our policies?</Text>
          <Group gap="xs">
            <Anchor href="/privacy" size="sm">Privacy</Anchor>
            <Text c="dimmed" size="sm">·</Text>
            <Anchor href="/terms" size="sm">Terms</Anchor>
            <Text c="dimmed" size="sm">·</Text>
            <Anchor href="/cookies" size="sm">Cookies</Anchor>
            <Text c="dimmed" size="sm">·</Text>
            <Anchor href="/accessibility" size="sm">Accessibility</Anchor>
          </Group>
        </Group>
      </Card>
    </PublicPageLayout>
  );
}
